import { motion } from 'framer-motion';
import {
  Bug,
  CheckCircle,
  AlertTriangle,
  ShieldAlert,
  Leaf
} from 'lucide-react';

export function DiseaseReport({ result }) {
  if (!result) return null;

  // Helper function to format recommendations into bullet points
  const formatRecommendations = text => {
    if (!text) return [];
    return text
      .split('**')
      .filter(item => item.trim())
      .map(item => item.trim().replace(/^\d+\.\s*/, ''));
  };

  // For healthy plants, parse the recommendations
  if (result.status === 'healthy') {
    const recommendations = formatRecommendations(result.recommendations);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-green-100 p-2 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              Analysis Results
            </h2>
          </div>

          <div className="bg-green-50 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <Leaf className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-green-800 mb-3">
                  Plant appears healthy
                </h3>
                <div className="space-y-4">
                  <div className="prose prose-green">
                    <h4 className="text-green-800 font-medium mb-2">
                      Care Recommendations:
                    </h4>
                    <ul className="space-y-2">
                      {recommendations.map((rec, index) => (
                        <li
                          key={index}
                          className="text-green-700 flex items-start gap-2"
                        >
                          <span className="text-green-500 mt-1">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // For disease detections, render the existing detailed report
  const renderSection = (title, content) => {
    if (!content) return null;

    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return null;

    return (
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <ul className="space-y-1">
          {lines.map((line, i) => {
            const cleanLine = line.replace(/^[-*•]\s*/, '').trim();
            if (!cleanLine) return null;
            return (
              <li key={i} className="flex items-start gap-2 text-gray-700">
                <span className="text-amber-600 mt-1.5">•</span>
                <span>{cleanLine}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-lg overflow-hidden"
    >
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-amber-100 p-2 rounded-full">
            <Bug className="w-6 h-6 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Analysis Results</h2>
        </div>

        {result.status === 'tentative' ? (
          <div className="bg-amber-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 font-medium mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-amber-800">Tentative Diagnosis</span>
            </div>
          </div>
        ) : (
          <div className="bg-red-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 font-medium mb-4">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              <span className="text-red-800">Disease Detected</span>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {result.analysis.split('\n\n').map((section, index) => {
            const [title, ...content] = section.split('\n');
            if (!title || !content.length) return null;
            return (
              <div key={index} className="space-y-4">
                {renderSection(
                  title.replace(/^\d+\.\s*/, '').replace(/:$/, ''),
                  content.join('\n')
                )}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
